import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { Lock, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import type { RootState } from "@/store";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useChangePassword } from "@/services/profileService";

const ChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state: RootState) => state.language);
  const navigate = useNavigate();

  const changePasswordMutation = useChangePassword();

  const [formData, setFormData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string>("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccessMessage("");

    // Basic validation
    if (!formData.current_password) {
      setErrors({
        current_password: t(
          "admin.change_password.current_required",
          "Current password is required"
        ),
      });
      return;
    }

    if (!formData.password) {
      setErrors({
        password: t(
          "admin.change_password.password_required",
          "New password is required"
        ),
      });
      return;
    }

    if (formData.password.length < 8) {
      setErrors({
        password: t(
          "admin.change_password.password_min",
          "Password must be at least 8 characters"
        ),
      });
      return;
    }

    if (formData.password !== formData.password_confirmation) {
      setErrors({
        password_confirmation: t(
          "admin.change_password.password_mismatch",
          "Passwords do not match"
        ),
      });
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        current_password: formData.current_password,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
      });
      // Success - reset form and show success message
      setFormData({
        current_password: "",
        password: "",
        password_confirmation: "",
      });
      setSuccessMessage(
        t(
          "admin.change_password.change_success",
          "Password changed successfully"
        )
      );
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error: unknown) {
      console.error("Error changing password:", error);
      if (error && typeof error === "object" && "response" in error) {
        const apiError = error as {
          response?: {
            data?: {
              errors?: Record<string, string | string[]>;
              message?: string;
            };
          };
        };
        if (apiError.response?.data?.errors) {
          // Normalize errors to strings (handle arrays by taking first element)
          const normalizedErrors: Record<string, string> = {};
          Object.entries(apiError.response.data.errors).forEach(
            ([key, value]) => {
              normalizedErrors[key] = Array.isArray(value) ? value[0] : value;
            }
          );
          setErrors(normalizedErrors);
        } else {
          setErrors({
            general:
              apiError.response?.data?.message ||
              t(
                "admin.change_password.change_error",
                "Failed to change password"
              ),
          });
        }
      } else {
        setErrors({
          general: t(
            "admin.change_password.change_error",
            "Failed to change password"
          ),
        });
      }
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t("admin.change_password.title", "Change Password")}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t(
              "admin.change_password.subtitle",
              "Update your password to keep your account secure"
            )}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate(`/${currentLanguage}/admin/profile`)}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {t("common.back", "Back")}
        </Button>
      </motion.div>

      {/* Success Message */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-200"
        >
          {successMessage}
        </motion.div>
      )}

      {/* Error Message */}
      {errors.general && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200"
        >
          {errors.general}
        </motion.div>
      )}

      {/* Change Password Form */}
      <form onSubmit={handleSubmit}>
        <div className="max-w-2xl">
          <Card className="bg-white dark:bg-gray-800 border-0 shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                {t("admin.change_password.form_title", "Change Password")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Password */}
              <div className="space-y-2">
                <Label htmlFor="current_password">
                  {t(
                    "admin.change_password.current_password",
                    "Current Password"
                  )}{" "}
                  *
                </Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showCurrentPassword ? "text" : "password"}
                    value={formData.current_password}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        current_password: e.target.value,
                      })
                    }
                    placeholder={t(
                      "admin.change_password.current_placeholder",
                      "Enter your current password"
                    )}
                    className={
                      errors.current_password ? "border-red-500 pr-10" : "pr-10"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showCurrentPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {errors.current_password && (
                  <p className="text-sm text-red-600">
                    {errors.current_password}
                  </p>
                )}
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="password">
                  {t("admin.change_password.new_password", "New Password")} *
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder={t(
                      "admin.change_password.new_placeholder",
                      "Enter your new password (min. 8 characters)"
                    )}
                    className={
                      errors.password ? "border-red-500 pr-10" : "pr-10"
                    }
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600">{errors.password}</p>
                )}
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t(
                    "admin.change_password.password_help",
                    "Password must be at least 8 characters long"
                  )}
                </p>
              </div>

              {/* Confirm New Password */}
              <div className="space-y-2">
                <Label htmlFor="password_confirmation">
                  {t(
                    "admin.change_password.confirm_password",
                    "Confirm New Password"
                  )}{" "}
                  *
                </Label>
                <div className="relative">
                  <Input
                    id="password_confirmation"
                    type={showPasswordConfirmation ? "text" : "password"}
                    value={formData.password_confirmation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        password_confirmation: e.target.value,
                      })
                    }
                    placeholder={t(
                      "admin.change_password.confirm_placeholder",
                      "Confirm your new password"
                    )}
                    className={
                      errors.password_confirmation
                        ? "border-red-500 pr-10"
                        : "pr-10"
                    }
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswordConfirmation(!showPasswordConfirmation)
                    }
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {showPasswordConfirmation ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {errors.password_confirmation && (
                  <p className="text-sm text-red-600">
                    {errors.password_confirmation}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={changePasswordMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  {changePasswordMutation.isPending
                    ? t("common.saving", "Saving...")
                    : t(
                        "admin.change_password.change_button",
                        "Change Password"
                      )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(`/${currentLanguage}/admin/profile`)}
                >
                  {t("common.cancel", "Cancel")}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
};

// Display name for debugging
ChangePassword.displayName = "ChangePassword";

export default ChangePassword;
